import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import { ChevronRight, InfoIcon } from "lucide-react";

import { BasicTooltip } from "../../Tooltip";
import { cn } from "../../utils";

import { ContextMenuItems } from "./types";
import { contextMenuIconVariants, contextMenuItemVariants } from "./variants";

interface ContextMenuItemProps<TData> {
  contextMenuItem: ContextMenuItems<TData>;
  data: TData;
}

export const ContextMenuItem = <TData,>({
  contextMenuItem,
  data,
}: ContextMenuItemProps<TData>) => {
  const {
    id,
    label,
    icon: Icon,
    className,
    getDisabled,
    getContent,
    preventCloseOnSelect,
    tooltipContent,
    infoTooltipContent,
    onClick,
    isDivider,
    variant,
    hidden,
    showIf,
    subItems,
  } = contextMenuItem;

  if (hidden) {
    return null;
  }

  if (showIf && !showIf(data)) {
    return null;
  }

  if (isDivider) {
    return <div key={id} className="my-1 h-px bg-gray-200" />;
  }

  const isDisabled = getDisabled?.(data);
  const shouldPreventClose =
    typeof preventCloseOnSelect === "function"
      ? preventCloseOnSelect(data)
      : preventCloseOnSelect;

  if (subItems?.length) {
    return (
      <ContextMenuPrimitive.Sub>
        <ContextMenuPrimitive.SubTrigger
          disabled={isDisabled}
          className={cn(
            contextMenuItemVariants({ variant: variant || "primary" }),
            isDisabled &&
              "!cursor-not-allowed opacity-50 hover:!bg-transparent",
            className,
          )}
        >
          <span className="flex w-full min-w-0 items-center text-sm">
            {Icon && (
              <span
                className={contextMenuIconVariants({
                  variant: variant || "primary",
                })}
              >
                <Icon className="h-5 w-5" />
              </span>
            )}
            <span className="min-w-0 flex-1 whitespace-nowrap">{label}</span>
            <ChevronRight className="ml-2 h-4 w-4 text-gray-500" aria-hidden />
          </span>
        </ContextMenuPrimitive.SubTrigger>
        <ContextMenuPrimitive.Portal>
          <ContextMenuPrimitive.SubContent
            sideOffset={4}
            className={cn(
              "z-50 flex min-w-[8rem] flex-col gap-1 overflow-hidden rounded-md border border-gray-300 bg-white px-3 py-2 shadow-lg",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            )}
          >
            {subItems.map((subItem) => (
              <ContextMenuItem
                key={subItem.id}
                contextMenuItem={subItem}
                data={data}
              />
            ))}
          </ContextMenuPrimitive.SubContent>
        </ContextMenuPrimitive.Portal>
      </ContextMenuPrimitive.Sub>
    );
  }

  if (getContent) {
    return (
      <ContextMenuPrimitive.Item
        className={cn(
          contextMenuItemVariants({ variant: variant || "primary" }),
          "cursor-default",
          isDisabled && "!cursor-not-allowed opacity-50 hover:!bg-transparent",
          className,
        )}
        key={id}
      >
        {getContent(data)}
      </ContextMenuPrimitive.Item>
    );
  }
  const tooltipMessage =
    typeof tooltipContent === "function"
      ? tooltipContent(data)
      : tooltipContent;
  const infoTooltipMessage =
    typeof infoTooltipContent === "function"
      ? infoTooltipContent(data)
      : infoTooltipContent;

  return (
    <ContextMenuPrimitive.Item
      className={cn(
        contextMenuItemVariants({ variant: variant || "primary" }),
        isDisabled && "!cursor-not-allowed opacity-50 hover:!bg-transparent",
        className,
      )}
      key={id}
      onSelect={(e) => {
        if (!isDisabled) {
          if (shouldPreventClose) {
            e.preventDefault();
          }
          onClick?.(data);
        }
      }}
    >
      <BasicTooltip
        shouldShow={Boolean(isDisabled && tooltipMessage)}
        content={tooltipMessage}
      >
        <span className="flex w-full min-w-0 items-center text-sm">
          {Icon && (
            <span
              className={contextMenuIconVariants({
                variant: variant || "primary",
              })}
            >
              <Icon className="h-5 w-5" />
            </span>
          )}
          <span className="min-w-0 flex-1 whitespace-nowrap">{label}</span>
          {infoTooltipMessage && (
            <BasicTooltip content={infoTooltipMessage}>
              <span
                className="ml-1 inline-flex shrink-0"
                onClick={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
              >
                <InfoIcon
                  className="h-4 w-4 text-gray-400 hover:text-gray-600"
                  aria-hidden
                />
              </span>
            </BasicTooltip>
          )}
        </span>
      </BasicTooltip>
    </ContextMenuPrimitive.Item>
  );
};
